import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { getFirestore, Timestamp, doc, setDoc, collection, getDocs } from "@firebase/firestore";
import { app } from '../../firebase.js';
import Form from 'react-bootstrap/Form';
import profilePictureContImage from '../../Images/defprofile.png';
import { getAuth } from "firebase/auth";
import swal from 'sweetalert';
import Button from 'react-bootstrap/Button';
import { Link, useNavigate } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import { logAdminActivity } from '../../utils/activityLogger';
import { buildReceiptEmail } from '../../utils/emailTemplates';

function AddIncomeComp() {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [results, setResults] = useState([]);
    const [studentsIndex, setStudentsIndex] = useState([]);
    const [amount, setAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const db = getFirestore(app);
    const auth = getAuth(app);
    const searchResultsRef = useRef(null);
    const lastSearchRef = useRef('');
    const searchLogTimeoutRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    let navigate = useNavigate();

    const formattedAmount = useMemo(() => (
        amount ? Number(amount).toLocaleString('en-KE') : '0'
    ), [amount]);

    const formattedPaymentDate = useMemo(() => {
        if (!paymentDate) {
            return 'Select a payment date';
        }

        const parsedDate = new Date(paymentDate);
        if (Number.isNaN(parsedDate.getTime())) {
            return 'Select a payment date';
        }

        return parsedDate.toLocaleString();
    }, [paymentDate]);

    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "users"));
                const allStudents = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    allStudents.push({
                        profilePic: data.profilePic || profilePictureContImage,
                        bio: data.bio || '',
                        userStatus: data.userStatus || '',
                        userId: data.userId,
                        course: data.course || '',
                        userEmail: data.userEmail || '',
                        phoneNumber: data.phoneNumber || ''
                    });
                });

                setStudentsIndex(allStudents);
            } catch (error) {
                console.error("Error loading students for search:", error);
            }
        };

        fetchStudents();
    }, [db]);


    const handleSearchChange = async (event) => {
        const value = event.target.value;
        setSearchValue(value);

        if (searchLogTimeoutRef.current) {
            clearTimeout(searchLogTimeoutRef.current);
        }
        searchLogTimeoutRef.current = setTimeout(() => {
            const trimmed = value.trim();
            if (!trimmed) return;
            if (trimmed === lastSearchRef.current) return;
            lastSearchRef.current = trimmed;
            logAdminActivity({
                action: 'Search',
                details: `Add Income search: "${trimmed}"`,
                meta: { page: 'Add Income', query: trimmed }
            });
        }, 600);

        const normalizedValue = value.trim().toLowerCase();

        if (normalizedValue !== "") {
            const searchResults = studentsIndex
                .filter((student) => {
                    const name = String(student.bio || '').toLowerCase();
                    const email = String(student.userEmail || '').toLowerCase();
                    const course = String(student.course || '').toLowerCase();
                    const phone = String(student.phoneNumber || '').toLowerCase();

                    return (
                        name.includes(normalizedValue) ||
                        email.includes(normalizedValue) ||
                        course.includes(normalizedValue) ||
                        phone.includes(normalizedValue)
                    );
                })
                .sort((studentA, studentB) => {
                    const nameA = String(studentA.bio || '').toLowerCase();
                    const nameB = String(studentB.bio || '').toLowerCase();
                    const startsA = nameA.startsWith(normalizedValue) ? 0 : 1;
                    const startsB = nameB.startsWith(normalizedValue) ? 0 : 1;

                    if (startsA !== startsB) {
                        return startsA - startsB;
                    }

                    return nameA.localeCompare(nameB);
                })
                .slice(0, 20);

            setResults(searchResults);
        } else {
            setResults([]);
        }
    };

    const onSelectStudent = (userId) => {
        const student = results.find(r => r.userId === userId);
        setSelectedStudent(student);
        setResults([]);
        setSearchValue(student.bio);
    };

    const viewSelectedStudent = useCallback(() => {
        if (!selectedStudent?.userId) {
            return;
        }

        navigate('/view-student', { state: { myData: selectedStudent.userId } });
    }, [navigate, selectedStudent]);

    const handleSubmitIncome = async () => {
        if (!selectedStudent || !amount || !paymentDate || !paymentMethod) {
            swal("Error", "Please fill in all the required fields", "error");
            return;
        }

        setIsSubmitting(true);

        const paymentTimestamp = new Date(paymentDate).getTime();

        const invoiceNumber = Math.random();
        const generatedReceiptNumner = Math.floor(invoiceNumber * 7971349);

        try {
            const newDataRef = doc(collection(db, "Income"));
            await setDoc(newDataRef, {
                studentId: selectedStudent.userId,
                incomeAmount: amount,
                paymentDate: Timestamp.fromMillis(paymentTimestamp),
                paymentMethod,
                createdBy: auth.currentUser.uid,
                timeCreated: Timestamp.now(),
                paymentId: newDataRef.id,
                invoiceNumber: generatedReceiptNumner
            }).then(() => {
                logAdminActivity({
                    action: 'Add Income',
                    details: `Recorded income for ${selectedStudent.bio} (KES ${amount})`,
                    meta: {
                        page: 'Add Income',
                        studentId: selectedStudent.userId,
                        studentName: selectedStudent.bio,
                        amount,
                        paymentMethod
                    }
                });


                // swal("Success", "Income recorded successfully!", "success");
                // // Optionally reset form:
                // setSelectedStudent(null);
                // setAmount('');
                // setPaymentDate('');
                // setPaymentMethod('');
                // setSearchValue('');

                const emailAddress = selectedStudent.userEmail;
                const emailSubject = "Payment Receipt #" + generatedReceiptNumner;

                const emailMessage = buildReceiptEmail({
                    studentName: selectedStudent.bio,
                    amount,
                    paymentDate: new Date(paymentTimestamp),
                    paymentMethod,
                    receiptNumber: generatedReceiptNumner
                });

                const emailData = {
                    email: emailAddress,
                    message: emailMessage,
                    subject: emailSubject
                };

                const auth = getAuth();
                const user = auth.currentUser;

                user.getIdToken().then((token) => {
                    fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        },
                        body: JSON.stringify(emailData)
                    })
                        .then(res => res.json())
                        .then(data => {

                            const studentsUserName = selectedStudent.bio;
                            const invnumb = generatedReceiptNumner;
                            const phoneNumber = selectedStudent.phoneNumber;

                            sendSMS(studentsUserName, amount, paymentDate, invnumb, phoneNumber, token);

                        })
                        .catch(error => {
                            console.error("Failed to send email:", error);
                        });
                });

            })

        } catch (error) {
            console.error("Error recording income:", error);
            setIsSubmitting(false);
            swal("Error", "Failed to record income", "error");

        }
    };

    const sendSMS = async (studentName, amount, paymentDate, receiptNumber, phoneNumber, token) => {
        const message = `Dear ${studentName}, we have received your payment of KES. ${amount} on ${paymentDate} . View your receipt here https://live.ubunifucollege.com//view-receipt?${receiptNumber}. Thank you for your timely payment. - Nutrition And Dietetics Academy`;
        const smsData = {
            phoneNumber: phoneNumber,
            message: message
        };

        try {
            const response = await fetch("https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(smsData)
            });

            await response.json();
            swal("Success", "Income recorded successfully!", "success");
            navigate("/income")
            return true;
        } catch (error) {
            console.error("Error sending SMS: ", error);
            swal("Success", "Income recorded successfully!", "success");
            navigate("/income");
            return false;
        }
    };

    return (
        <div className='clientsComp addIncomePage'>
            <div className="addIncomeHero">
                <div className="addIncomeHeroCopy">
                    <h5>Record School Fee Income</h5>
                    <p className="addIncomeHeroText">
                        Search for a student, confirm their details, then capture the payment amount, date, and method in one clean flow.
                    </p>
                </div>
                <div className="addIncomeHeroMeta">
                    <span>{selectedStudent ? 'Student selected' : 'Waiting for student selection'}</span>
                </div>
            </div>

            <div className="addIncomeLayout">
                <div className="addIncomeMainCard">
                    <div className="addIncomeSectionHeader">
                        <div>
                            <h6>Student Search</h6>
                            <p>Find the student whose payment you are recording.</p>
                        </div>
                        <Link to="/income" className="btn btn-outline-dark addIncomeGhostBtn">
                            View Income Records
                        </Link>
                    </div>

                    <Form.Group className="mb-0 addIncomeSearchGroup">
                        <Form.Label>Search for a student</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Type the student's name"
                            value={searchValue}
                            onChange={handleSearchChange}
                        />
                        {results.length > 0 && (
                            <div className='searchResultsStud addIncomeSearchResults' ref={searchResultsRef}>
                                {results.map((result, index) => (
                                    <div key={index} className='resultItem' onClick={() => onSelectStudent(result.userId)}>
                                        <div className='resultItemDiv'>
                                            <img src={result.profilePic} alt={`${result.bio}'s profile`} />
                                        </div>
                                        <div>
                                            <p>{result.bio}</p>
                                            <p className='statusResult'>{result.userStatus}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    <div className="addIncomeSelectedCard">
                        <div className="addIncomeSectionHeader addIncomeSectionHeaderCompact">
                            <div>
                                <h6>Selected Student</h6>
                                <p>Review the student before recording the payment.</p>
                            </div>
                        </div>

                        {selectedStudent ? (
                            <div className="addIncomeStudentSummary">
                                <div className="addIncomeStudentIdentity">
                                    <div className="addIncomeStudentAvatar">
                                        <img src={selectedStudent.profilePic || profilePictureContImage} alt={`${selectedStudent.bio}'s profile`} />
                                    </div>
                                    <div>
                                        <h6>{selectedStudent.bio}</h6>
                                        <p>{selectedStudent.userEmail || 'No email recorded'}</p>
                                    </div>
                                </div>
                                <div className="addIncomeStudentMeta">
                                    <span className="addIncomeMetaPill">{selectedStudent.userStatus || 'Status unavailable'}</span>
                                    <span className="addIncomeMetaPill">{selectedStudent.course || 'Course unavailable'}</span>
                                    <Button variant="outline-dark" className="addIncomeGhostBtn" onClick={viewSelectedStudent}>
                                        View student
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="addIncomeEmptyHint">
                                Select a student from the search results to continue.
                            </div>
                        )}
                    </div>

                    <div className="addIncomeFormCard">
                        <div className="addIncomeSectionHeader addIncomeSectionHeaderCompact">
                            <div>
                                <h6>Payment Details</h6>
                                <p>Capture the payment amount, date, and method.</p>
                            </div>
                        </div>

                        <div className="addIncomeFormGrid">
                            <Form.Group className="mb-0">
                                <Form.Label>Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Enter payment amount"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-0">
                                <Form.Label>Payment Date</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                />
                            </Form.Group>

                            <Form.Group className="mb-0 addIncomeFormFullWidth">
                                <Form.Label>Payment Method</Form.Label>
                                <Form.Select
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                >
                                    <option value="">Select payment method</option>
                                    <option value="Mpesa">Mpesa</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="Cash">Cash</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Bank Account Deposit">Bank Account Deposit</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    </div>
                </div>

                <div className="addIncomeSideCard">
                    <div className="addIncomeSectionHeader addIncomeSectionHeaderCompact">
                        <div>
                            <h6>Payment Preview</h6>
                            <p>A quick summary before saving.</p>
                        </div>
                    </div>

                    <div className="addIncomePreviewBlock">
                        <span className="addIncomePreviewLabel">Student</span>
                        <strong>{selectedStudent?.bio || 'No student selected'}</strong>
                    </div>

                    <div className="addIncomePreviewBlock">
                        <span className="addIncomePreviewLabel">Amount</span>
                        <strong>KES {formattedAmount}</strong>
                    </div>

                    <div className="addIncomePreviewBlock">
                        <span className="addIncomePreviewLabel">Payment Date</span>
                        <strong>{formattedPaymentDate}</strong>
                    </div>

                    <div className="addIncomePreviewBlock">
                        <span className="addIncomePreviewLabel">Payment Method</span>
                        <strong>{paymentMethod || 'Select payment method'}</strong>
                    </div>

                    <div className="addIncomePreviewNote">
                        A receipt will be prepared and shared with the student through the available communication channels.
                    </div>

                    <Button
                        className='w-100 addIncomeSubmitBtn'
                        onClick={handleSubmitIncome}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                />{' '}
                                Recording...
                            </>
                        ) : (
                            'Record Income'
                        )}
                    </Button>
                </div>
            </div>

        </div>
    );
}

export default AddIncomeComp;
